import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import JurisprudenceResultItem from './JurisprudenceResultItem';
import { apiClient } from '../../../../utils/apiClient';

interface JurisprudenceSearchProps {
    onSelect: (reference: string) => void;
    isVisible: boolean;
    setIsVisible: (visible: boolean) => void;
    initialSearchQuery?: string;
}

interface SearchResult {
    summary?: string;
    number?: string;
    decision_date?: string;
    jurisdiction?: string;
    chamber?: string;
    solution?: string;
    id?: string;
    location?: string;

    // Nouveaux champs de scoring
    enhancedScore?: number;
    scoreBreakdown?: {
        exactMatch: number;
        jurisdictionMatch: number;
        recency: number;
        textRelevance: number;
        judilibreScore: number;
    };
    relevanceTier?: 'high' | 'medium' | 'low';
}

// Cache simple pour éviter les recherches répétées
const searchCache = new Map<string, SearchResult[]>();

const JurisprudenceSearch: React.FC<JurisprudenceSearchProps> = ({ onSelect, isVisible, setIsVisible, initialSearchQuery = '' }) => {
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // États pour filtres et tri
    const [sortBy, setSortBy] = useState<'relevance' | 'date' | 'jurisdiction'>('relevance');
    const [filterJurisdiction, setFilterJurisdiction] = useState<string>('all');

    // Références pour le debouncing
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastSearchQuery = useRef<string>('');

    // État pour gérer la largeur du modal avec drag
    const [modalWidth, setModalWidth] = useState<number>(330); // Largeur par défaut
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [dragStartX, setDragStartX] = useState<number>(0);
    const [dragStartWidth, setDragStartWidth] = useState<number>(0);
    const [isResizing, setIsResizing] = useState<boolean>(false); // Flag pour bloquer le onClick
    const [isMaximized, setIsMaximized] = useState<boolean>(false);
    const [previousWidth, setPreviousWidth] = useState<number>(modalWidth);
    const [clickStartX, setClickStartX] = useState<number>(0);

    // Limites de largeur
    const MIN_WIDTH = 320;
    const MAX_WIDTH = 1000;

    // Fonction pour toggle maximiser/restaurer
    const handleClick = useCallback(() => {
        if (isMaximized) {
            setModalWidth(previousWidth);
            setIsMaximized(false);
        } else {
            setPreviousWidth(modalWidth);
            setModalWidth(MAX_WIDTH);
            setIsMaximized(true);
        }
    }, [isMaximized, modalWidth, previousWidth, MAX_WIDTH]);

    // Gestion du début du drag
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
        setIsResizing(true);
        setDragStartX(e.clientX);
        setClickStartX(e.clientX);
        setDragStartWidth(modalWidth);
    }, [modalWidth]);

    // Mémoïser le handler de clic extérieur
    const handleClickOutside = useCallback((event: MouseEvent) => {
        // Ne fermer que si on ne vient PAS de redimensionner
        if (isResizing) return;

        const target = event.target as HTMLElement;
        if (!target.closest('.jurisprudence-sidebar') && !target.closest('.jurisprudence-button')) {
            setIsVisible(false);
        }
    }, [isResizing, setIsVisible]);

    // Gestion du drag en cours
    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e: MouseEvent) => {
            // Calculer le delta (négatif car on tire vers la gauche pour agrandir)
            const delta = dragStartX - e.clientX;
            const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, dragStartWidth + delta));
            setModalWidth(newWidth);

            // Si on a bougé, désactiver l'état maximisé
            if (Math.abs(delta) > 5) {
                setIsMaximized(false);
            }
        };

        const handleMouseUp = (e: MouseEvent) => {
            setIsDragging(false);

            // Différencier clic vs drag : si mouvement < 5px, c'est un clic
            const deltaMovement = Math.abs(clickStartX - e.clientX);
            if (deltaMovement < 5) {
                handleClick();
            }

            // Désactiver le flag après un court délai pour éviter le onClick de l'overlay
            setTimeout(() => {
                setIsResizing(false);
            }, 50);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragStartX, dragStartWidth, clickStartX, handleClick, MIN_WIDTH, MAX_WIDTH]);

    // Effet pour gérer les clics extérieurs
    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleClickOutside]);

    // Nettoyage du timeout au démontage
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    // Fonction de recherche avec debouncing et cache
    const performSearch = useCallback(async (query: string) => {
        if (!query.trim()) return;

        // Vérifier le cache d'abord
        if (searchCache.has(query)) {
            const cachedResults = searchCache.get(query)!;
            setSearchResults(cachedResults);
            setIsLoading(false);
            return;
        }

        try {
            const response = await apiClient('/jurisprudences/search', {
                method: 'POST',
                body: JSON.stringify({ query }),
            });

            if (!response.ok) {
                throw new Error('Erreur lors de la recherche de jurisprudence');
            }

            const data = await response.json();

            // Mettre en cache les résultats (limiter le cache à 50 entrées)
            if (searchCache.size >= 50) {
                const firstKey = searchCache.keys().next().value;
                if (firstKey !== undefined) {
                    searchCache.delete(firstKey);
                }
            }
            searchCache.set(query, data);

            setSearchResults(data);
        } catch (error) {
            console.error('Erreur lors de la requête API interne:', error);
            alert('Erreur lors de la recherche de jurisprudence. Veuillez réessayer.');
            setSearchResults([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Lancer la recherche automatiquement quand le modal devient visible
    useEffect(() => {
        if (isVisible && initialSearchQuery) {
            setIsLoading(true);
            lastSearchQuery.current = initialSearchQuery;
            performSearch(initialSearchQuery);
        } else if (isVisible && !initialSearchQuery) {
        }
    }, [isVisible, initialSearchQuery, performSearch]);


    // Handler de sélection de résultat
    const handleResultSelect = useCallback((result: SearchResult) => {
        onSelect(result.id || '');
        setIsVisible(false);
    }, [onSelect, setIsVisible]);

    // Handler pour fermer la modal
    const handleCloseModal = useCallback(() => {
        // Ne fermer que si on ne vient PAS de redimensionner
        if (!isResizing) {
            setIsVisible(false);
        }
    }, [isResizing, setIsVisible]);

    // Mémoïser la fonction de génération de titre
    const generateTitle = useCallback((result: SearchResult) => {
        if (result.jurisdiction === 'Cour d\'appel') {
            const locationPart = result.location
                ? `de ${result.location.replace(/^Cour d'appel de /, '')}`
                : '';
            return `${result.decision_date || 'Date inconnue'} - Cour d'appel ${locationPart} ${result.number ? `- RG n° ${result.number}` : ''}`.trim();
        }
        return `${result.decision_date || 'Date inconnue'} - ${result.jurisdiction || 'Juridiction inconnue'} ${result.number ? `n° ${result.number}` : ''}`.trim();
    }, []);

    // Extraire les juridictions uniques pour le filtre
    const availableJurisdictions = useMemo(() => {
        const jurisdictions = new Set<string>();
        searchResults.forEach(result => {
            if (result.jurisdiction) {
                jurisdictions.add(result.jurisdiction);
            }
        });
        return Array.from(jurisdictions).sort();
    }, [searchResults]);

    // Appliquer les filtres et le tri
    const filteredAndSortedResults = useMemo(() => {
        let filtered = [...searchResults];

        // Filtrer par juridiction
        if (filterJurisdiction !== 'all') {
            filtered = filtered.filter(r => r.jurisdiction === filterJurisdiction);
        }

        // Trier
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'relevance':
                    return (b.enhancedScore || 0) - (a.enhancedScore || 0);
                case 'date':
                    const dateA = a.decision_date ? new Date(a.decision_date).getTime() : 0;
                    const dateB = b.decision_date ? new Date(b.decision_date).getTime() : 0;
                    return dateB - dateA; // Plus récent en premier
                case 'jurisdiction':
                    return (a.jurisdiction || '').localeCompare(b.jurisdiction || '');
                default:
                    return 0;
            }
        });

        return filtered;
    }, [searchResults, filterJurisdiction, sortBy]);

    // Stats des résultats
    const resultStats = useMemo(() => {
        const total = searchResults.length;
        const high = searchResults.filter(r => r.relevanceTier === 'high').length;
        const medium = searchResults.filter(r => r.relevanceTier === 'medium').length;
        const low = searchResults.filter(r => r.relevanceTier === 'low').length;
        return { total, high, medium, low };
    }, [searchResults]);

    return (
        <>
            {isVisible && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-20 z-[10000] transition-opacity duration-300"
                    onClick={handleCloseModal}
                >
                    <div
                        className="jurisprudence-sidebar fixed inset-y-0 right-0 bg-mcdm-primary-100 dark:bg-mcdm-third-400 shadow-lg z-[10010] overflow-y-auto"
                        style={{
                            width: `${modalWidth}px`,
                            transition: isDragging ? 'none' : 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Handle de redimensionnement sur le bord gauche */}
                        <div
                            onMouseDown={handleMouseDown}
                            className={`absolute top-0 left-0 bottom-0 w-1 cursor-ew-resize hover:bg-mcdm-secondary-400 transition-colors duration-200 group ${
                                isDragging ? 'bg-mcdm-secondary-500' : 'bg-transparent'
                            }`}
                            style={{ zIndex: 10020 }}
                        />

                        {/* Indicateur visuel fixé au centre de l'écran */}
                        <div
                            onMouseDown={handleMouseDown}
                            className={`fixed w-1 h-16 bg-mcdm-secondary-400 dark:bg-mcdm-secondary-300 rounded-full cursor-ew-resize transition-opacity duration-200 ${
                                isDragging ? 'opacity-100' : 'opacity-40 hover:opacity-100'
                            }`}
                            style={{
                                top: '50vh',
                                right: `${modalWidth}px`,
                                transform: 'translate(50%, -50%)',
                                zIndex: 10030,
                                transition: isDragging ? 'opacity 0.2s' : 'right 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s',
                            }}
                        >
                            {/* Points décoratifs */}
                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col gap-1">
                                <div className="w-1 h-1 bg-white rounded-full"></div>
                                <div className="w-1 h-1 bg-white rounded-full"></div>
                                <div className="w-1 h-1 bg-white rounded-full"></div>
                            </div>
                        </div>

                        {/* Contenu du modal */}
                        <div className="p-4 flex flex-col h-full">
                            {/* Header avec stats et toggle filtres */}
                            {!isLoading && searchResults.length > 0 && (
                                <div className="mb-4 mt-12">
                                    <div className="mb-2">
                                        <h2 className="font-platypi text-mcdm-third-500 text-lg dark:text-mcdm-primary-100">
                                            Jurisprudences ({filteredAndSortedResults.length}/{resultStats.total})
                                        </h2>
                                    </div>

                                    {/* Texte de recherche */}
                                    <p className="text-sm text-mcdm-third-400 dark:text-mcdm-primary-100 mb-3">
                                        Résultats de recherche pour : "{initialSearchQuery}"
                                    </p>

                                    {/* Filtres et tri (toujours visibles) */}
                                    <div className="bg-mcdm-primary-100 dark:bg-mcdm-third-500 rounded-lg space-y-3">
                                        {/* Filtres en 2 colonnes */}
                                        <div className="grid grid-cols-2 gap-3">
                                            {/* Tri */}
                                            <div>
                                                <label className="block text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 mb-1">
                                                    Trier par:
                                                </label>
                                                <select
                                                    value={sortBy}
                                                    onChange={(e) => setSortBy(e.target.value as any)}
                                                    className="w-full px-2 py-1 text-sm border border-mcdm-primary-300 dark:border-mcdm-primary-200 rounded bg-white dark:bg-mcdm-third-400 text-mcdm-third-500 dark:text-mcdm-primary-100"
                                                >
                                                    <option value="relevance">Pertinence</option>
                                                    <option value="date">Date (plus récent)</option>
                                                    <option value="jurisdiction">Juridiction</option>
                                                </select>
                                            </div>

                                            {/* Filtre juridiction */}
                                            <div>
                                                <label className="block text-sm font-medium text-mcdm-third-500 dark:text-mcdm-primary-100 mb-1">
                                                    Juridiction:
                                                </label>
                                                <select
                                                    value={filterJurisdiction}
                                                    onChange={(e) => setFilterJurisdiction(e.target.value)}
                                                    className="w-full px-2 py-1 text-sm border border-mcdm-primary-300 dark:border-mcdm-primary-200 rounded bg-white dark:bg-mcdm-third-400 text-mcdm-third-500 dark:text-mcdm-primary-100"
                                                >
                                                    <option value="all">Toutes</option>
                                                    {availableJurisdictions.map(j => (
                                                        <option key={j} value={j}>{j}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        {/* Reset filters */}
                                        {(filterJurisdiction !== 'all' || sortBy !== 'relevance') && (
                                            <button
                                                onClick={() => {
                                                    setFilterJurisdiction('all');
                                                    setSortBy('relevance');
                                                }}
                                                className="w-full px-2 py-1 text-sm bg-mcdm-primary-200 dark:bg-mcdm-third-400 text-mcdm-third-500 dark:text-mcdm-primary-100 rounded hover:bg-mcdm-primary-300 dark:hover:bg-mcdm-third-300 transition-colors"
                                            >
                                                Réinitialiser les filtres
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {isLoading ? (
                                // Loading skeletons
                                <div className="mt-16">
                                    {Array(5).fill(0).map((_, index) => (
                                        <div key={index} className="mb-4 animate-pulse">
                                            <div className="h-4 bg-mcdm-primary-200 dark:bg-mcdm-primary-100 rounded w-full mb-2"></div>
                                            <div className="space-y-2">
                                                {Array(8).fill(0).map((_, i) => (
                                                    <div key={i} className="h-3 bg-mcdm-primary-200 dark:bg-mcdm-primary-100 rounded w-full"></div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : filteredAndSortedResults.length === 0 && searchResults.length > 0 ? (
                                // Aucun résultat après filtrage
                                <div className="flex-1 flex items-center justify-center">
                                    <div className="text-center text-gray-500 dark:text-gray-400">
                                        <p className="text-lg mb-2">Aucun résultat avec ces filtres</p>
                                        <button
                                            onClick={() => {
                                                setFilterJurisdiction('all');
                                            }}
                                            className="text-sm text-mcdm-secondary-400 hover:underline"
                                        >
                                            Réinitialiser les filtres
                                        </button>
                                    </div>
                                </div>
                            ) : filteredAndSortedResults.length === 0 ? (
                                // Aucun résultat de recherche
                                <div className="flex-1 flex items-center justify-center">
                                    <div className="text-center text-gray-500 dark:text-gray-400">
                                        <p className="text-lg">Aucun résultat trouvé</p>
                                    </div>
                                </div>
                            ) : (
                                // Résultats de recherche
                                <div className="space-y-3 flex-1 overflow-y-auto">
                                    {filteredAndSortedResults.map((result, index) => (
                                        <JurisprudenceResultItem
                                            key={result.id || index}
                                            result={result}
                                            title={generateTitle(result)}
                                            onClick={() => handleResultSelect(result)}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default JurisprudenceSearch;